/**
 * ApproveUserDialog — admin approves a pending user, sets role and permissions.
 */

import { useState } from 'react';
import Modal from '@/components/common/Modal';
import Button from '@/components/common/Button';
import PermissionsEditor from './PermissionsEditor';
import type { CmsUser, UserRole, UserPermissions } from '@/types';
import { getDefaultPermissionsForRole } from '@/utils/permissionUtils';

interface ApproveUserDialogProps {
  user:    CmsUser;
  open:    boolean;
  onClose: () => void;
  onApprove: (uid: string, role: UserRole, permissions: UserPermissions) => void;
  loading: boolean;
}

export default function ApproveUserDialog({
  user, open, onClose, onApprove, loading,
}: ApproveUserDialogProps) {
  const [role, setRole] = useState<UserRole>('editor');
  const [permissions, setPermissions] = useState<UserPermissions>(
    getDefaultPermissionsForRole('editor')
  );

  const handleRoleChange = (newRole: UserRole) => {
    setRole(newRole);
    setPermissions(getDefaultPermissionsForRole(newRole));
  };

  const handleApprove = () => {
    onApprove(user.uid, role, permissions);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Approve — ${user.displayName}`}
      size="xl"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleApprove} loading={loading}>
            Approve user
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* User info */}
        <div className="rounded-lg bg-surface-50 px-4 py-3 text-sm text-surface-600">
          <span className="font-medium text-surface-900">{user.displayName}</span>
          {' · '}{user.email}
        </div>

        {/* Role selector */}
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-2">Role</label>
          <div className="flex gap-3">
            {(['admin', 'editor', 'viewer'] as UserRole[]).map(r => (
              <button
                key={r}
                onClick={() => handleRoleChange(r)}
                className={`flex-1 rounded-lg border py-2 text-sm font-medium capitalize transition-colors ${
                  role === r
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-surface-200 text-surface-600 hover:bg-surface-50'
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        {/* Permissions — hidden for admin (they get everything) */}
        {role !== 'admin' && (
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-2">Permissions</label>
            <PermissionsEditor
              permissions={permissions}
              onChange={setPermissions}
            />
          </div>
        )}

        {role === 'admin' && (
          <p className="text-sm text-surface-500 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            Admins have full access to all audiences and features.
          </p>
        )}
      </div>
    </Modal>
  );
}
